import { Injectable } from "@angular/core";

import * as fromRoot from "#core/router-store";
import { Store, select } from "@ngrx/store";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, iif, of } from "rxjs";
import {
    catchError,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { LanguageHelper } from "#core/helpers/language.helper";
import * as assignmentsActions from "#core/teacher-classes/store/actions/assignments.action";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { MatDialog } from "@angular/material/dialog";
import { Asset } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-angular-cure";
import { AssignmentsService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { PPAssignment, PPUser } from "cli-types";

@Injectable()
export class AssignmentsEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromRoot.CustomSerializer>,
        private assignmentsService: AssignmentsService,
        private assetsService: AssetsService,
        private snackBarService: SnackBarService,
        private localStorage: LocalStorage,
        private languageHelper: LanguageHelper,
        public dialog: MatDialog
    ) {}

    // Calls service to retrieve all assignments that need eval across all active teacher-classes for teacher
    loadNeedEvalAssignments$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadNeedEvalAssignments))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .getNeedsEvalAssignments(action.paginationParams)
                        .pipe(
                            map((response) =>
                                assignmentsActions.LoadNeedEvalAssignmentsSuccess(
                                    { payload: response }
                                )
                            ),
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadNeedEvalAssignmentsFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    // Calls service to retrieve all assignments for a user in a particular teacher-class
    loadEvalAssignments$ = createEffect(() =>
        this.actions$.pipe(ofType(assignmentsActions.LoadEvalAssignments)).pipe(
            // get studentID from url
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    return {
                        ...action.payload,
                        studentID: router
                            ? router.state.params.userID ||
                              action.payload.studentID
                            : null,
                        courseID: router ? router.state.params.courseID : null,
                    };
                }
            ),
            switchMap((params) => {
                if (params.studentID === null && params.courseID === null) {
                    return of(
                        assignmentsActions.LoadEvalAssignmentsFail({
                            payload: "no action",
                        })
                    );
                } else {
                    return this.assignmentsService
                        .getCourseUserAssignments(params)
                        .pipe(
                            map((assignments) => assignments),
                            map((response) =>
                                assignmentsActions.LoadEvalAssignmentsSuccess({
                                    payload: response,
                                })
                            ),
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadEvalAssignmentsFail(
                                        error
                                    )
                                )
                            )
                        );
                }
            })
        )
    );

    // Calls service to retrieve assignments in a teacher-class
    loadCourseAssignments$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(
                    assignmentsActions.LoadCourseAssignments,
                    assignmentsActions.loadAssignmentsAssignedInEditionCourses
                )
            )
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        if (router) {
                            return {
                                ...action.payload,
                                courseID: action.courseId
                                    ? action.courseId
                                    : router.state.params.courseID,
                            };
                        } else {
                            return action.payload;
                        }
                    }
                ),
                mergeMap((params) => {
                    return this.assignmentsService
                        .getCourseAssignments(params)
                        .pipe(
                            map((assignments) => assignments),
                            map((response) =>
                                assignmentsActions.LoadCourseAssignmentsSuccess(
                                    { payload: response }
                                )
                            ),
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadCourseAssignmentsFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    // Calls service to update an assignment detail for a student
    updateAssignment$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.UpdateStudentProfileAssignment))
            .pipe(
                switchMap((data) => {
                    return this.assignmentsService
                        .updateAssignment(data.params, data.assignmentID)
                        .pipe(
                            switchMap((response) => [
                                assignmentsActions.UpdateStudentProfileAssignmentSuccess(
                                    { payload: response }
                                ),
                                assignmentsActions.LoadAssignmentsCourseMetrics(
                                    {
                                        courseID: response.courseID,
                                        assetId: response.assetId,
                                        shortAssignableType:
                                            response.shortAssignableType,
                                    }
                                ),
                            ]),
                            tap((response) => {
                                this.snackBarService.success(
                                    "Assignment updated!"
                                );
                            }),
                            catchError((error) => {
                                this.snackBarService.error(
                                    "Assignment update failed!"
                                );
                                return of(
                                    assignmentsActions.UpdateStudentProfileAssignmentFail(
                                        error
                                    )
                                );
                            })
                        );
                })
            )
    );

    // Calls service to delete an assignment in a teacher-class
    deleteCourseAssignment$ = createEffect(() =>
        this.actions$.pipe(ofType(assignmentsActions.DeleteAssignment)).pipe(
            switchMap((action) => {
                return this.assignmentsService
                    .deleteCourseAssignment(action.payload)
                    .pipe(
                        map((assignments) => assignments),
                        map((response) => {
                            this.snackBarService.success("Assignment deleted.");
                            return assignmentsActions.DeleteAssignmentSuccess({
                                assignmentID: action.payload.assignmentID,
                            });
                        }),
                        catchError((error) => {
                            this.snackBarService.error(
                                "Assignment delete failed!"
                            );
                            return of(
                                assignmentsActions.DeleteAssignmentFail(error)
                            );
                        })
                    );
            })
        )
    );

    loadMetrics$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadAssignmentsCourseMetrics))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .getCourseMetrics(
                            action.courseID,
                            action.assetId,
                            action.shortAssignableType
                        )
                        .pipe(
                            map((courseMetrics) =>
                                assignmentsActions.LoadAssignmentsCourseMetricsSuccess(
                                    {
                                        courseID: action.courseID,
                                        assetId: action.assetId,
                                        shortAssignableType:
                                            action.shortAssignableType,
                                        courseMetrics: courseMetrics,
                                    }
                                )
                            ),
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadAssignmentsCourseMetricsFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    updateCourse$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.UpdateCourseAssignment))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .updateCourseAssignment(
                            action.assetId,
                            action.courseID,
                            action.shortAssignableType,
                            action.payload
                        )
                        .pipe(
                            map((results) => {
                                this.snackBarService.success(
                                    "Assignment updated!"
                                );
                                return assignmentsActions.UpdateCourseAssignmentSuccess(
                                    { payload: results }
                                );
                            }),
                            catchError((error) =>
                                of(
                                    assignmentsActions.UpdateCourseAssignmentFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    loadAssignmentAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assignmentsActions.LoadAssignmentAsset)).pipe(
            switchMap((action) => {
                return this.assetsService.getSelectedAsset(action.assetId).pipe(
                    map((asset) =>
                        assignmentsActions.LoadAssignmentAssetSuccess({
                            assignmentId: action.assignmentId,
                            asset: asset,
                        })
                    ),
                    catchError((error) =>
                        of(assignmentsActions.LoadAssignmentAssetFail(error))
                    )
                );
            })
        )
    );

    loadAllAssignmentAssets$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadAllAssignmentAssets))
            .pipe(
                switchMap((action) => {
                    const assetIds = action.assignments
                        .map((a) => {
                            // if(a.compositeTaskAssignments) {
                            //     const ids = a.compositeTaskAssignments.map( t => t.assetId);
                            //     return [...ids, a.assetId];
                            // }else
                            return a.assetId;
                        })
                        .flat()
                        .filter((id) => id !== "");
                    const params: FeathersServicePaginationParameters<Asset> = {
                        limit: 200,
                        skip: 0,
                        filter: { "assetId[$in][]": assetIds },
                        sorting: {},
                    };
                    // here; call 1 and call 2; why are we calling this twice with the same data?
                    return this.assetsService.getAssets(params).pipe(
                        map((assets) =>
                            assignmentsActions.LoadAllAssignmentAssetsSuccess({
                                assignments: action.assignments,
                                assets: assets.data,
                            })
                        ),
                        catchError((error) =>
                            of(
                                assignmentsActions.LoadAllAssignmentAssetsFail(
                                    error
                                )
                            )
                        )
                    );
                })
            )
    );

    // Routing effect for successful create and update
    handleUpdateSuccess$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.UpdateCourseAssignmentSuccess))
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        return {
                            courseID: router.state.params.courseID,
                        };
                    }
                ),
                map((routerPayload) => {
                    return new fromRoot.Go({
                        path: [
                            `teacher/classes/${routerPayload.courseID}/assignments`,
                        ],
                    });
                })
            )
    );

    previewStudentAssignmentActivity$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.previewStudentAssignmentActivity))
            .pipe(
                // get Asset if it's missing in assignment
                switchMap(({ assignment, componentRef }) => {
                    return iif(
                        () => !assignment.asset,
                        this.getAssignmentWithAsset(assignment),
                        of(assignment)
                    ).pipe(
                        // if previewing math asset, need to fetch its standards array to helper
                        switchMap((assignmentWithAsset) => {
                            const isMath =
                                !!assignmentWithAsset.asset &&
                                this.languageHelper.ifMathLanguage(
                                    assignmentWithAsset.asset.subjectId
                                );
                            return iif(
                                () => isMath,
                                this.getMathAssetStandards(assignmentWithAsset),
                                of(assignmentWithAsset)
                            );
                        }),
                        // open preview dialog
                        switchMap((assignmentWithAsset) => {
                            const user: PPUser | undefined =
                                this.localStorage.get("userProfile");
                            return this.dialog
                                .open(componentRef(), {
                                    data: {
                                        asset: assignmentWithAsset.asset,
                                        standards:
                                            [],
                                        user,
                                    },
                                })
                                .afterClosed()
                                .pipe(
                                    map(() =>
                                        assignmentsActions.closedPreviewAssetDialog()
                                    )
                                );
                        })
                    );
                }),
                catchError((error) =>
                    of(
                        assignmentsActions.previewStudentAssignmentActivityFail(
                            error
                        )
                    )
                )
            )
    );

    private getAssignmentWithAsset(
        assignment: PPAssignment
    ): Observable<PPAssignment> {
        return this.assetsService.getSelectedAsset(assignment.assetId).pipe(
            map((asset) => ({
                ...assignment,
                asset,
            }))
        );
    }

    private getMathAssetStandards(
        assignment: PPAssignment
    ): Observable<PPAssignment> {
            return of(assignment);
    }
}
